// --- Ungated brand saver helpers ---
// Both inputs already arrive over the wire, so this is frontend-only:
//   * brand      -> the products response (/load_products_by_page/.../retail/)
//   * restricted -> /mobile_app_check_restrictions/batch (see checkGatedBatch in utils.js)
// A product is ungated (green unlock icon) when its batch entry has an empty
// `restrictions` array.

// The canonical brand for a product, or "" when the row has none.
// No title guessing: a missing brand is reported as missing, not invented.
export const getProductBrand = (product) => {
	if (!product || typeof product.brand !== "string") {
		return "";
	}
	const brand = product.brand.trim();
	// The DB column is nullable and some rows carry placeholder text.
	if (brand === "" || brand.toLowerCase() === "n/a" || brand.toLowerCase() === "null") {
		return "";
	}
	return brand;
};

// Same shape the tables use to paint the lock icon: empty restrictions = ungated.
export const isAsinUngated = (gatedStatus, asin) => {
	return (
		!!gatedStatus &&
		gatedStatus.hasOwnProperty(asin) &&
		Array.isArray(gatedStatus[asin].restrictions) &&
		gatedStatus[asin].restrictions.length === 0
	);
};

const SAVER_ENABLED_KEY = "saveUngatedBrands";
const BRANDS_LIST_KEY = "ungatedBrandsList";
// The ASINs that were ungated but carried no brand — stored as a list of ASINs,
// not as a running count: the same product is re-seen every time its page is
// re-loaded (paging back and forth, a reload, a re-render of the same rows), and
// a counter would keep incrementing on each sighting. Counting the ASINs makes
// the number idempotent no matter how often a product is seen.
const MISSING_ASINS_KEY = "ungatedBrandsMissingAsins";

export const isBrandSaverEnabled = () => {
	return localStorage.getItem(SAVER_ENABLED_KEY) === "1";
};

export const setBrandSaverEnabled = (bool) => {
	localStorage.setItem(SAVER_ENABLED_KEY, bool ? "1" : "0");
};

export const getSavedBrands = () => {
	try {
		const parsed = JSON.parse(localStorage.getItem(BRANDS_LIST_KEY));
		return Array.isArray(parsed) ? parsed : [];
	} catch (e) {
		return [];
	}
};

const getMissingAsins = () => {
	try {
		const parsed = JSON.parse(localStorage.getItem(MISSING_ASINS_KEY));
		return Array.isArray(parsed) ? parsed : [];
	} catch (e) {
		return [];
	}
};

// How many distinct ungated products were skipped for having no brand. Surfaced
// in the UI so an empty-brand backend response is visible instead of silent.
export const getMissingBrandCount = () => getMissingAsins().length;

// Merge the given brands into the stored list with case-insensitive dedupe
// (first-seen casing wins), and record `missingAsins` (ungated products with no
// brand) as a set. `foundAsins` are ungated products that DID have a brand this
// pass — they're dropped from the missing set so an ASIN first seen brandless and
// later seen with a brand isn't double-counted as skipped. Returns the brand list.
export const saveUngatedBrands = (brands, missingAsins = [], foundAsins = []) => {
	const existing = getSavedBrands();
	const seen = {};
	const merged = [];
	[...existing, ...(Array.isArray(brands) ? brands : [])].forEach((b) => {
		if (!b || typeof b !== "string") {
			return;
		}
		const key = b.toLowerCase();
		if (!seen[key]) {
			seen[key] = true;
			merged.push(b);
		}
	});
	localStorage.setItem(BRANDS_LIST_KEY, JSON.stringify(merged));

	const found = new Set(Array.isArray(foundAsins) ? foundAsins : []);
	// Reconcile: an ASIN that now has a brand is no longer "missing".
	let mergedMissing = getMissingAsins().filter((asin) => !found.has(asin));
	(Array.isArray(missingAsins) ? missingAsins : []).forEach((asin) => {
		if (asin && !found.has(asin) && mergedMissing.indexOf(asin) === -1) {
			mergedMissing.push(asin);
		}
	});
	localStorage.setItem(MISSING_ASINS_KEY, JSON.stringify(mergedMissing));

	// Announce the write so any open UI reflects it without re-reading on a guessed
	// schedule: React runs child effects before parent ones, so a child polling
	// localStorage after a parent's save would always be one render behind.
	window.dispatchEvent(new CustomEvent("nepeto:brandsSaved", {
		detail: { brands: merged, missing: mergedMissing.length },
	}));
	return merged;
};

export const clearSavedBrands = () => {
	localStorage.removeItem(BRANDS_LIST_KEY);
	localStorage.removeItem(MISSING_ASINS_KEY);
};
